/**
 * Rahmen des Leitfadens — pure Teile
 * (docs/planungsphase-leitfaden-plan.md, Stufe L2).
 *
 * Was `useLeitfaden`, `PlanungReiter`, `PlanungBausteine` und der
 * Verlassen-Schutz in `PlanungModul` entscheiden, steht hier als reine
 * Funktion, damit es ohne DOM pruefbar ist (kein jsdom).
 * Die Komponenten lesen nur noch ab.
 */

import type { PlanungReiterSchluessel } from './planungAnsicht';
import type { UebergabeSprungziel } from './uebergabeLogic';
import type { StationSchluessel } from './leitfadenTypen';

// ═══════════════════════════════════════════════════════════════════════════
// ANGEZEIGTE STATION
// ═══════════════════════════════════════════════════════════════════════════

/** Die Station, die der Nutzer gewaehlt hat — je Projekt, damit ein
 *  Projektwechsel im selben Modul nicht die Wahl des alten mitnimmt. */
export interface StationWahl {
    projectId: string;
    station: StationSchluessel;
}

/**
 * Gewaehlt schlaegt errechnet. Bis zur ersten Beruehrung gibt es keine Wahl,
 * dann FOLGT die angezeigte Station den Daten (`startStation`); danach steht
 * sie, bis der Nutzer selbst wechselt — eine Antwort, die Station 6 erledigt,
 * reisst ihn nicht weg.
 */
export const angezeigteStation = (
    wahl: StationWahl | null,
    projectId: string,
    errechnet: StationSchluessel,
): StationSchluessel => (wahl && wahl.projectId === projectId ? wahl.station : errechnet);

/** Was `fixiere()` setzt: die gerade angezeigte Station als Wahl — oder
 *  `null`, wenn fuer dieses Projekt schon gewaehlt ist (dann nichts tun). */
export const fixierteWahl = (
    wahl: StationWahl | null,
    projectId: string,
    angezeigt: StationSchluessel,
): StationWahl | null => (wahl && wahl.projectId === projectId ? null : { projectId, station: angezeigt });

// ═══════════════════════════════════════════════════════════════════════════
// HILFEKAESTEN
// ═══════════════════════════════════════════════════════════════════════════

/** Weggeklickt im Browser-Speicher (beim Oeffnen gelesen) oder in dieser Sitzung. */
export const hilfeIstAus = (
    id: string,
    gespeichert: ReadonlySet<string>,
    sitzung: ReadonlySet<string>,
): boolean => gespeichert.has(id) || sitzung.has(id);

// ═══════════════════════════════════════════════════════════════════════════
// VERLASSEN-SCHUTZ (Plan 2.2)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Wohin das Verlassen fuehrt: zurueck ins Projekt (Zurueck-Pfeil, „Zum
 * Projekt") oder in das Modul einer Folgeunterlage (Übergabe „Zum Modul",
 * Fassungsrueckfrage „Neue Fassung anlegen"). Beide Wege gehen ueber
 * denselben Schutz — beide haengen das Modul aus.
 */
export type VerlassenZiel = 'projekt' | UebergabeSprungziel;

/**
 *   entwurf               — ungespeicherte Grundlagen
 *   ki                    — eine KI-Auswertung der Baubeschreibung laeuft; das
 *                           Aushaengen der KI-Leiste bricht sie ab
 *   ki_und_entwurf        — beides
 *   entwurf_abgeschlossen — ungespeicherte Grundlagen in einer ABGESCHLOSSENEN
 *                           Phase: sie lassen sich nicht mehr speichern (die
 *                           Phase nimmt keine Grundlagen mehr an), es bleibt
 *                           nur Verwerfen oder Hierbleiben
 */
export type VerlassenGrund = 'entwurf' | 'ki' | 'ki_und_entwurf' | 'entwurf_abgeschlossen';

/**
 * Warum vor dem Verlassen gefragt werden muss — `null`: nichts geht
 * verloren, es geht sofort. Nie in der Nur-Ansicht: dort gibt es nichts zu
 * speichern, und die KI-Leiste ist aus (ein Ergebnis liesse sich ohnehin
 * nicht uebernehmen). In der abgeschlossenen Phase wird nichts mehr
 * gespeichert — ein Entwurf dort ist nur noch zu verwerfen.
 */
export const verlassenGrund = (lage: {
    schmutzig: boolean;
    isReadOnly: boolean;
    kiLaeuft: boolean;
    abgeschlossen: boolean;
}): VerlassenGrund | null => {
    if (lage.isReadOnly) return null;
    if (lage.abgeschlossen) return lage.schmutzig ? 'entwurf_abgeschlossen' : lage.kiLaeuft ? 'ki' : null;
    if (lage.kiLaeuft) return lage.schmutzig ? 'ki_und_entwurf' : 'ki';
    return lage.schmutzig ? 'entwurf' : null;
};

/** Die gestellte Frage: fuer welchen Entwurf (Objektgleichheit) und wohin. */
export interface VerlassenFrage<T> {
    fuer: T;
    ziel: VerlassenZiel;
}

/**
 * Steht die Verlassen-Leiste — und mit welchem Grund? Sie gehoert zu dem
 * Entwurf, fuer den gefragt wurde (`frage.fuer`, Objektgleichheit): wer
 * weitertippt, hat sich fuers Bleiben entschieden, und nach dem Speichern ist
 * der Entwurf ein neuer — die Leiste verschwindet in beiden Faellen von
 * selbst, ohne Effekt, und taucht nicht spaeter ungefragt wieder auf.
 *
 * Der Grund ist der JETZIGE (`verlassenGrund`), nicht der beim Fragen: endet
 * die KI-Auswertung, waehrend die Leiste steht, bleibt nur der Entwurf als
 * Grund — oder keiner, dann ist die Leiste weg und „Zum Projekt" geht beim
 * naechsten Klick sofort. Gegangen wird nie von selbst.
 */
export const verlassenLeisteOffen = <T>(
    frage: VerlassenFrage<T> | null,
    entwurf: T,
    grund: VerlassenGrund | null,
): { ziel: VerlassenZiel; grund: VerlassenGrund } | null =>
    frage !== null && frage.fuer === entwurf && grund !== null ? { ziel: frage.ziel, grund } : null;

/** „in den SiGe-Plan" — das Modul hinter dem Sprungziel, fuer die Leiste. */
const ZIEL_WECHSEL: Record<UebergabeSprungziel, string> = {
    vorank: 'in die Vorankündigung',
    unterlage: 'in die Unterlage für spätere Arbeiten',
    sigeplan: 'in den SiGe-Plan',
    asplan: 'in den A+S-Plan',
    bauordnung: 'in die Baustellenordnung',
    handbuch: 'in das SiGe-Handbuch',
};

export interface VerlassenLeisteText {
    satz: string;
    /** Knopf „speichern, dann gehen" — `null`, wenn es nichts zu speichern gibt. */
    speichern: string | null;
    /** Knopf „ohne Speichern gehen". */
    gehen: string;
}

/**
 * Was die Leiste sagt und wie ihre Knoepfe heissen. Das Ziel steht im Satz
 * („… vor dem Wechsel in den SiGe-Plan"); „Hierbleiben" gibt es immer.
 */
export const verlassenLeisteText = (grund: VerlassenGrund, ziel: VerlassenZiel): VerlassenLeisteText => {
    const entwurf = ziel === 'projekt'
        ? 'Ungespeicherte Angaben in den Grundlagen.'
        : `Ungespeicherte Angaben in den Grundlagen — vor dem Wechsel ${ZIEL_WECHSEL[ziel]}.`;
    const ki = ziel === 'projekt'
        ? 'Die KI-Auswertung läuft noch — Verlassen bricht sie ab.'
        : `Die KI-Auswertung läuft noch — der Wechsel ${ZIEL_WECHSEL[ziel]} bricht sie ab.`;
    switch (grund) {
        case 'entwurf':
            return { satz: entwurf, speichern: 'Speichern und verlassen', gehen: 'Verwerfen und verlassen' };
        case 'ki':
            return { satz: ki, speichern: null, gehen: 'Abbrechen und verlassen' };
        case 'ki_und_entwurf':
            return { satz: `${entwurf} ${ki}`, speichern: 'Speichern und verlassen', gehen: 'Verwerfen und verlassen' };
        case 'entwurf_abgeschlossen':
            return {
                satz: `${entwurf} ${ENTWURF_ABGESCHLOSSEN_SATZ}`,
                speichern: null,
                gehen: 'Verwerfen und verlassen',
            };
    }
};

/** Zusatz der Verlassen-Leiste in der abgeschlossenen Phase. */
export const ENTWURF_ABGESCHLOSSEN_SATZ =
    'Die Planungsphase ist abgeschlossen; sie werden nicht mehr gespeichert.';

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHLUSS-RUECKFRAGE (Freigabe-Gate)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Satz der Abschluss-Rueckfrage, solange die Grundlagen ungespeichert sind —
 * statt der Haken. Abgeschlossen wird der GESPEICHERTE Stand: ein Entwurf,
 * der danach noch in der Maske stuende, liesse sich in der abgeschlossenen
 * Phase nicht mehr speichern.
 */
export const ABSCHLUSS_UNGESPEICHERT =
    'Die Grundlagen sind noch nicht gespeichert; abgeschlossen wird der gespeicherte Stand.';

/**
 * Ist die Abschluss-Rueckfrage offen? Abgeleitet, ohne Effekt: nur solange
 * sie gestellt ist UND das Gate offen UND die Phase nicht abgeschlossen ist.
 * Schliesst das Gate, waehrend sie steht, ist sie zu.
 */
export const abschlussNachfrageOffen = (lage: {
    nachfrage: boolean;
    freigabeMoeglich: boolean;
    abgeschlossen: boolean;
}): boolean => lage.nachfrage && lage.freigabeMoeglich && !lage.abgeschlossen;

// ═══════════════════════════════════════════════════════════════════════════
// SPAETE SERVER-ANTWORTEN (Fragen aendern)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Jede Aenderung an einer Frage schickt ein eigenes UPDATE, und die Antworten
 * kommen nicht zwingend in der Reihenfolge der Aenderungen zurueck. Je
 * Frage-Id zaehlt eine Laufnummer mit; eine Antwort wird nur uebernommen,
 * wenn sie zur JUENGSTEN Aenderung dieser Frage gehoert — sonst ersetzte die
 * spaete Antwort auf eine aeltere Eingabe die neuere in der Oberflaeche.
 */
export const starteFrageLauf = (laeufe: Map<string, number>, id: string): number => {
    const nr = (laeufe.get(id) ?? 0) + 1;
    laeufe.set(id, nr);
    return nr;
};

/** Gehoert die Antwort mit Laufnummer `nr` noch zur juengsten Aenderung an `id`? */
export const istJuengsterFrageLauf = (laeufe: ReadonlyMap<string, number>, id: string, nr: number): boolean =>
    laeufe.get(id) === nr;

// ═══════════════════════════════════════════════════════════════════════════
// LAUFENDE KI-AUSWERTUNG (Station 1, Reiter Grundlagen)
// ═══════════════════════════════════════════════════════════════════════════

/** Hinweis in der Fussleiste und `title` der gesperrten Knoepfe. */
export const KI_WECHSEL_HINWEIS = 'KI-Auswertung läuft — bitte warten, ein Wechsel bricht sie ab.';

/**
 * Warum Stationswechsel, Ablage und Ansichts-Umschalter gerade gesperrt sind
 * — oder `null`. Die KI-Leiste bricht ihren Lauf ab, sobald sie ausgehaengt
 * wird; jeder Wechsel weg von Station 1 bzw. vom Reiter Grundlagen haengt sie
 * aus. „Zum Projekt" sperrt nicht, sondern fragt (`verlassenGrund`).
 */
export const wechselSperre = (kiLaeuft: boolean): string | null => (kiLaeuft ? KI_WECHSEL_HINWEIS : null);

/** Der Reiter, in dem die KI-Leiste haengt. */
export const KI_REITER: PlanungReiterSchluessel = 'grundlagen';

/**
 * Darf der Reiter gewechselt werden? Waehrend die KI liest, nur auf den
 * Reiter, in dem sie haengt (dort steht man dann ohnehin) — jeder andere
 * haengte sie aus. Gilt auch fuer Wechsel aus dem Code („Im Katalog
 * ansehen", Katalog erzeugt).
 */
export const reiterWechselErlaubt = (kiLaeuft: boolean, ziel: PlanungReiterSchluessel): boolean =>
    !kiLaeuft || ziel === KI_REITER;
